import { api } from './client';

/**
 * Группы у куратора по подтипам марафонов (D40, договор
 * `docs/CONTRACTS/curator-groups.md`). Сервер — `users-service`, на проде с
 * 12.09.2026.
 *
 * ⚠️ РОЛЬ СТРОГО `curator`. У админа своя админка групп, эти ручки ему
 * отвечают 403. Курс, группа или ученик вне подтипов куратора выглядят
 * несуществующими — 404, а не «нет прав» (договор §0).
 *
 * ⚠️ `confirm` КЛАДЁМ В ТЕЛО, а не в `?confirm=true` (сервер принимает оба).
 * Сторож `adminRoutesInGateway.test.ts` режет путь по «?» и сверял бы с картой
 * шлюза обрезанный путь — то есть не то, что уходит на сервер.
 */

/** Куратор группы или личный куратор ученика — только id и имя. */
export type CuratorPerson = { id: string; name: string };

/** Курс подтипа куратора: что видно в списке «Мои марафоны». */
export type CuratorSubtypeCourse = {
  id: string;
  title: string;
  subtype: string;
  access_category: string | null;
  status: string;
  /** Записи не снятые: `inactive` сервер не считает. */
  students: number;
  groups: number;
};

/**
 * Строка ученика курса.
 *
 * ⚠️ `mine` СЧИТАЕТСЯ ТОЛЬКО ПО ГРУППЕ (`curator_groups.go`, `leadsGroup`):
 * личный ученик самого куратора приходит с `mine: false`. Вид строки на экране
 * считает `lib/curatorGroupRows.ts`, а не это поле напрямую.
 *
 * Почты здесь нет намеренно: куратор видит весь подтип, а не только своих.
 */
export type CuratorRosterRow = {
  /** ID ДЛЯ ДОБАВЛЕНИЯ И УДАЛЕНИЯ — работа идёт с записью на курс, не с учёткой. */
  enrollment_id: string;
  student_id: string;
  student_name: string;
  status: string;
  access_until: string | null;
  group_id: string | null;
  group_name: string | null;
  group_curators: CuratorPerson[];
  /** Личный куратор ученика без группы (D25). */
  curator_id: string | null;
  curator_name: string | null;
  mine: boolean;
};

export type CuratorGroup = {
  id: string;
  marathon_id: string;
  name: string;
  capacity: number;
  /** Кто завёл группу; у групп админки и старых автогрупп — `null`. */
  created_by: string | null;
  created_at: string;
  member_count: number;
  curators: CuratorPerson[];
  mine: boolean;
};

export type AssignResult = {
  /** Записей в группе ПОСЛЕ вызова — включая тех, кто уже был в ней. */
  assigned: number;
  /** У скольких снят личный куратор (D25). */
  curator_cleared: number;
  /** Сколько учеников ушло от других кураторов — им ушла строка ленты. */
  transferred: number;
};

export type RemoveResult = { removed: number; curator_cleared: number };

/** Чужой ученик из отказа 409 `student_in_other_group`. */
export type GroupConflict = {
  enrollment_id: string;
  student_id: string;
  student_name: string;
  group_id: string | null;
  group_name: string | null;
  curators: CuratorPerson[];
};

type List<T> = { items: T[]; total: number };

export const curatorGroupsApi = {
  /** Курсы подтипов куратора. Нет подтипов — пустой список, не ошибка. */
  async listSubtypeCourses(): Promise<CuratorSubtypeCourse[]> {
    const { data } = await api.get('/curator/marathons/subtypes');
    return (data as List<CuratorSubtypeCourse>)?.items ?? [];
  },

  async listStudents(marathonId: string): Promise<CuratorRosterRow[]> {
    const { data } = await api.get(`/curator/marathons/${marathonId}/students`);
    return (data as List<CuratorRosterRow>)?.items ?? [];
  },

  /** Ученики СВОЕЙ группы (чужая — 403). Нужны статистике сторис: «кто смотрел» — по аудитории истории. */
  async groupMembers(groupId: string): Promise<CuratorRosterRow[]> {
    const { data } = await api.get(`/curator/groups/${groupId}/members`);
    return (data as List<CuratorRosterRow>)?.items ?? [];
  },

  async listGroups(marathonId: string): Promise<CuratorGroup[]> {
    const { data } = await api.get(`/curator/marathons/${marathonId}/groups`);
    return (data as List<CuratorGroup>)?.items ?? [];
  },

  async createGroup(marathonId: string, name: string, capacity?: number): Promise<CuratorGroup> {
    const { data } = await api.post(`/curator/marathons/${marathonId}/groups`, {
      name,
      ...(typeof capacity === 'number' ? { capacity } : {}),
    });
    return data as CuratorGroup;
  },

  /**
   * Добавить или забрать. Первый вызов — всегда без подтверждения: кто «чужой»,
   * решает сервер под замком записи, а не экран. На 409 показываем список и
   * повторяем ТОТ ЖЕ вызов с `confirm: true`.
   *
   * Пачка проверяется целиком: отказ по любой записи — не пишется ничего.
   */
  async addMembers(groupId: string, enrollmentIds: string[], confirm = false): Promise<AssignResult> {
    const { data } = await api.post(`/curator/groups/${groupId}/members`, {
      enrollment_ids: enrollmentIds,
      confirm,
    });
    return data as AssignResult;
  },

  /** Убрать из СВОЕЙ группы: ученик остаётся без группы и без куратора (§5). */
  async removeMembers(groupId: string, enrollmentIds: string[]): Promise<RemoveResult> {
    const { data } = await api.post(`/curator/groups/${groupId}/members/remove`, {
      enrollment_ids: enrollmentIds,
    });
    return data as RemoveResult;
  },
};
